// src/components/Sobre.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CATEGORIAS_CONFIG, buscarNoticias } from "./Mapa";
import Carregando from "./Carregando";
import { CREDITOS, FOTO_CAPA, FOTO_CIDADE, FOTO_ONIBUS, FOTO_RUA } from "./fotosSobre";

// Revela os elementos marcados com data-revelar quando entram na tela. A
// classe sobre-animar só é aplicada aqui, então sem script nada fica oculto.
// `conteudo` refaz a varredura quando surgem elementos novos (as barras dos
// temas só existem depois que os dados chegam).
function useRevelar(raiz, conteudo) {
  useEffect(() => {
    const el = raiz.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    el.classList.add("sobre-animar");
    const observador = new IntersectionObserver((entradas) => {
      entradas.forEach((entrada) => {
        if (entrada.isIntersecting) {
          entrada.target.classList.add("revelado");
          observador.unobserve(entrada.target);
        }
      });
    }, { threshold: 0.15 });
    el.querySelectorAll("[data-revelar]:not(.revelado)").forEach((alvo) => observador.observe(alvo));
    return () => observador.disconnect();
  }, [raiz, conteudo]);
}

function Sobre({ abrirMapa }) {
  const raiz = useRef(null);
  const [noticias, setNoticias] = useState(null);

  useEffect(() => {
    buscarNoticias()
      .then((dados) => setNoticias(Array.isArray(dados) ? dados : []))
      .catch(() => setNoticias([]));
  }, []);

  // Contagens calculadas a partir das mesmas notícias exibidas no mapa.
  const resumo = useMemo(() => {
    if (!noticias) return null;
    const porCategoria = {};
    const municipios = new Set();
    noticias.forEach((n) => {
      porCategoria[n.categoria] = (porCategoria[n.categoria] || 0) + 1;
      if (n.municipio) municipios.add(n.municipio);
    });
    const categorias = Object.entries(porCategoria)
      .filter(([id]) => CATEGORIAS_CONFIG[id] && id !== "outros")
      .sort((a, b) => b[1] - a[1]);
    return {
      total: noticias.length,
      municipios: municipios.size,
      categorias,
      maior: categorias.length ? categorias[0][1] : 1,
    };
  }, [noticias]);
  useRevelar(raiz, resumo);

  return (
    <div className="sobre" ref={raiz}>
      <section className="sobre-abertura">
        <div className="sobre-abertura-texto">
          <h1 data-revelar>Planejamento e Conflitos Urbanos</h1>
          <p className="sobre-declaracao" data-revelar>
            Assessoria popular, produção do espaço e política urbana em
            Curitiba e região metropolitana.
          </p>
          <button className="sobre-acao" onClick={() => abrirMapa()} data-revelar>
            Explorar o mapa
          </button>
        </div>
        <figure className="sobre-abertura-foto">
          <img src={FOTO_CAPA.src} alt={FOTO_CAPA.alt} />
        </figure>
      </section>

      <section className="sobre-numeros" aria-live="polite">
        <h2 data-revelar>
          {resumo && resumo.total > 0
            ? `${resumo.total} conflitos no mapa`
            : "Os conflitos no mapa"}
        </h2>
        {resumo ? (
          <p data-revelar>
            {resumo.total > 0
              ? `Contados agora a partir das notícias publicadas, em ${resumo.municipios} municípios. Escolha um tema para vê-lo no mapa.`
              : "Não foi possível carregar os números agora. O mapa mostra todas as notícias registradas."}
          </p>
        ) : (
          <Carregando texto="Carregando os números do mapa…" />
        )}
        {resumo && resumo.categorias.length > 0 && (
          <ul className="sobre-temas">
            {resumo.categorias.map(([id, quantidade], indice) => (
              <li key={id} data-revelar style={{ "--atraso": `${indice * 70}ms` }}>
                <button
                  className="sobre-tema"
                  onClick={() => abrirMapa(id)}
                  style={{
                    "--cor-tema": CATEGORIAS_CONFIG[id].cor,
                    "--proporcao": quantidade / resumo.maior,
                  }}
                >
                  <span className="sobre-tema-nome">{CATEGORIAS_CONFIG[id].nome}</span>
                  <span className="sobre-tema-barra" aria-hidden="true" />
                  <span className="sobre-tema-total">{quantidade}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="sobre-projeto">
        <figure className="sobre-projeto-foto" data-revelar>
          <img src={FOTO_CIDADE.src} alt={FOTO_CIDADE.alt} loading="lazy" />
        </figure>
        <div className="sobre-projeto-texto">
          <h2 data-revelar>Um observatório da cidade em conflito</h2>
          <p data-revelar>
            Integrante da Rede de Observatórios de Conflitos Urbanos, o projeto
            tem como objetivo registrar, sistematizar, classificar e prover
            informações sobre as múltiplas e diversas manifestações da
            conflitualidade na cidade, por meio de sistema de informações
            geográficas.
          </p>
          <p data-revelar>
            Além de identificar os conflitos, visa compreender quais são seus
            desdobramentos e as transformações que produzem na cidade e na
            política urbana. Também envolve a análise e acompanhamento de ações
            de planejamento urbano em contexto de conflito social.
          </p>
        </div>
      </section>

      <section className="sobre-faixa" aria-label="Curitiba">
        {[FOTO_RUA, FOTO_ONIBUS].map((foto) => (
          <figure key={foto.src} data-revelar>
            <img src={foto.src} alt={foto.alt} loading="lazy" />
            <figcaption>{foto.legenda}</figcaption>
          </figure>
        ))}
      </section>

      <section className="sobre-plano">
        <h2 data-revelar>Notícias que viram dados</h2>
        <p className="sobre-declaracao" data-revelar>
          Plano de trabalho PIBITI: rastreamento automático (data crawling) de
          notícias para obtenção de dados de protestos e mapeamento e análise
          de conflitos urbanos em Curitiba e aglomerado metropolitano.
        </p>
        <p data-revelar>
          Um crawler percorre portais de notícias, um classificador separa o que
          é conflito urbano e identifica o tema, e cada notícia é localizada
          pelo bairro ou município que menciona.
        </p>
      </section>

      <footer className="sobre-equipe">
        <h2 data-revelar>Equipe</h2>
        <div className="sobre-pessoas">
          <div className="sobre-pessoa" data-revelar>
            <p className="sobre-nome">Rafael Ribeiro Kluge</p>
            <p>Desenvolvimento</p>
            <a href="mailto:rrk24@inf.ufpr.br">rrk24@inf.ufpr.br</a>
          </div>
          <div className="sobre-pessoa" data-revelar>
            <p className="sobre-nome">Prof. José Ricardo Vargas de Faria</p>
            <p>Orientação</p>
            <a href="mailto:jrvfaria@ufpr.br">jrvfaria@ufpr.br</a>
          </div>
        </div>
        <p className="sobre-creditos">
          Fotos via Wikimedia Commons:{" "}
          {CREDITOS.map((foto, indice) => (
            <React.Fragment key={foto.src}>
              {indice > 0 && "; "}
              <a href={foto.pagina} target="_blank" rel="noreferrer">{foto.credito}</a>
            </React.Fragment>
          ))}
          .
        </p>
      </footer>
    </div>
  );
}

export default Sobre;
